import React, { useRef, useState } from 'react';
import { Breadcrumb, Button, Form, Modal } from 'react-bootstrap';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { FadeModal, FadeTable, OverlayLink } from '../Global/Transitions';
import { CloudUploadIcon, DeleteIcon, DownloadIcon } from '../Global/Icons';
import { FileItem, FolderNode } from '../../Models/FileShare';
import {
  createFolder,
  deleteFile,
  deleteFolder,
  getDownloadUrl,
  getUploadUrl,
  uploadFile,
} from '../../Services/FileShareApiService';

const FolderIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#f59e0b" stroke="#d97706" strokeWidth="1.5"
    style={{ display: 'inline', flexShrink: 0 }}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const FileIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"
    style={{ display: 'inline', flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

interface Props {
  rootNode: FolderNode;
  label: string;
  section: 'users' | 'shared' | 'projects';
  projectId?: string;
  onRefresh: () => Promise<void>;
  instance: IPublicClientApplication;
  account: AccountInfo;
  uploading: boolean;
  setUploading: (v: boolean) => void;
  setError: (v: boolean) => void;
}

function walkPath(node: FolderNode, path: string[]): FolderNode {
  let current = node;
  for (const seg of path) {
    const next = current.folders[seg];
    if (!next) return { files: [], folders: {} };
    current = next;
  }
  return current;
}

export const FolderBrowser: React.FC<Props> = ({
  rootNode, label, section, projectId,
  onRefresh, instance, account, uploading, setUploading, setError,
}) => {
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentNode = walkPath(rootNode, currentPath);

  const navigateInto = (name: string) => setCurrentPath(prev => [...prev, name]);
  const navigateTo = (idx: number) => setCurrentPath(prev => prev.slice(0, idx));
  const closeModal = () => { setCreatingFolder(false); setNewFolderName(''); setSubmitting(false); };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setSubmitting(true);
    try {
      await createFolder(instance, account, section, [...currentPath, name].join('/'), projectId);
      await onRefresh();
      closeModal();
    } catch { setError(true); }
    finally { setSubmitting(false); }
  };

  const handleDeleteFolder = async (name: string) => {
    if (!window.confirm(`Delete folder "${name}" and all its contents?`)) return;
    try {
      await deleteFolder(instance, account, section, [...currentPath, name].join('/'), projectId);
      await onRefresh();
    } catch { setError(true); }
  };

  const handleDownload = async (file: FileItem) => {
    try {
      const url = await getDownloadUrl(instance, account, file.blobPath);
      const blob = await fetch(url).then(r => r.blob());
      const localUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = localUrl;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(localUrl);
    } catch { setError(true); }
  };

  const handleDeleteFile = async (file: FileItem) => {
    if (!window.confirm(`Delete "${file.name}"?`)) return;
    try {
      await deleteFile(instance, account, file.blobPath);
      await onRefresh();
    } catch { setError(true); }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const folderPath = currentPath.length > 0 ? currentPath.join('/') : undefined;
      const sasUrl = await getUploadUrl(instance, account, section, file.name, projectId, folderPath);
      await uploadFile(sasUrl, file);
      await onRefresh();
    } catch { setError(true); }
    finally { setUploading(false); }
  };

  const folderNames = Object.keys(currentNode.folders).sort();
  const files = [...currentNode.files].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <Breadcrumb className='mb-2'>
        <Breadcrumb.Item
          onClick={() => navigateTo(0)}
          active={currentPath.length === 0}
          style={{ cursor: currentPath.length > 0 ? 'pointer' : 'default' }}
        >
          {label}
        </Breadcrumb.Item>
        {currentPath.map((seg, idx) => (
          <Breadcrumb.Item
            key={`${seg}-${idx}`}
            onClick={() => navigateTo(idx + 1)}
            active={idx === currentPath.length - 1}
            style={{ cursor: idx < currentPath.length - 1 ? 'pointer' : 'default' }}
          >
            {seg}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>

      <input
        type='file'
        ref={fileInputRef}
        className='d-none'
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) { e.target.value = ''; handleUpload(f); }
        }}
      />

      <div className='mb-3'>
        <Button
          size='sm'
          variant='link'
          className='text-dark text-decoration-none p-0 d-inline-flex align-items-center gap-1'
          onClick={() => { setCreatingFolder(true); setNewFolderName(''); }}
          disabled={creatingFolder || uploading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            <line x1="12" y1="11" x2="12" y2="17" />
            <line x1="9" y1="14" x2="15" y2="14" />
          </svg>
          Create Folder
        </Button>
      </div>

      <FadeModal show={creatingFolder} title='New Folder' onHide={closeModal}>
        <Modal.Body>
          <Form.Label>Folder name</Form.Label>
          <Form.Control
            autoFocus
            placeholder='Folder name…'
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleCreateFolder();
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={closeModal}>Cancel</Button>
          <Button variant='primary' onClick={handleCreateFolder} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create Folder'}
          </Button>
        </Modal.Footer>
      </FadeModal>

      <FadeTable small>
        <thead className='bg-lighter'>
          <tr>
            <th>Name</th>
            <th className='text-end'>Size</th>
            <th className='text-end'>Modified</th>
            <th colSpan={2} />
          </tr>
        </thead>
        <tbody>
          {folderNames.length === 0 && files.length === 0 && (
            <tr>
              <td colSpan={5} className='text-center text-muted py-4'>No files here yet.</td>
            </tr>
          )}
          {folderNames.map(name => (
            <tr
              key={`folder-${name}`}
              style={{ cursor: 'pointer' }}
              onClick={() => navigateInto(name)}
            >
              <td><span className='d-inline-flex align-items-center gap-2'><FolderIcon />{name}</span></td>
              <td /><td /><td />
              <td width={40} onClick={e => e.stopPropagation()}>
                <OverlayLink
                  body={<DeleteIcon />}
                  placement='left'
                  className='text-danger'
                  onClick={() => handleDeleteFolder(name)}
                />
              </td>
            </tr>
          ))}
          {files.map(file => (
            <tr key={file.blobPath}>
              <td><span className='d-inline-flex align-items-center gap-2'><FileIcon />{file.name}</span></td>
              <td className='text-end text-muted'>{formatSize(file.size)}</td>
              <td className='text-end text-muted'>{formatDate(file.lastModified)}</td>
              <td width={40}>
                <OverlayLink
                  body={<DownloadIcon />}
                  placement='left'
                  onClick={() => handleDownload(file)}
                />
              </td>
              <td width={40}>
                <OverlayLink
                  body={<DeleteIcon />}
                  placement='left'
                  className='text-danger'
                  onClick={() => handleDeleteFile(file)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </FadeTable>

      <div
        style={{
          border: '2px dashed #6c757d',
          borderRadius: 6,
          padding: '12px 16px',
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          color: 'var(--bs-secondary-color)',
          background: 'var(--bs-secondary-bg)',
          userSelect: 'none',
          opacity: uploading ? 0.5 : 1,
          marginTop: 8,
          transition: 'all 0.15s ease',
        }}
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f && !uploading) handleUpload(f);
        }}
      >
        <CloudUploadIcon style={{ display: 'block', margin: '0 auto 8px' }} width={26} />
        <div style={{ fontSize: '0.875rem' }}>
          {uploading ? 'Uploading…' : 'Drop a file here or click to browse'}
        </div>
      </div>
    </>
  );
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}
